import React, { useEffect, useState } from "react";
import { Phone, Clock, TrendingUp, Activity, GitBranch } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

function StatCard({ icon: Icon, label, value, suffix = "", testid }) {
  return (
    <div className="glass rounded-2xl p-6 hover:-translate-y-0.5 transition" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">{label}</div>
        <div className="h-9 w-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Icon className="h-4 w-4 text-magenta" />
        </div>
      </div>
      <div className="mt-3 text-3xl font-extrabold text-white tracking-tight font-mono">
        {value}{suffix}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    completed: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
    escalated: "text-cyan-accent bg-cyan-accent/10 border-cyan-accent/20",
    missed: "text-rose-400 bg-rose-400/10 border-rose-400/20",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border ${styles[status] || "text-slate-400 bg-white/5 border-white/10"}`}>{status}</span>;
}

export default function Dashboard() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [calls, setCalls] = useState([]);
  const [series, setSeries] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, c, ts] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/calls"),
        api.get("/dashboard/timeseries"),
      ]);
      setStats(s.data);
      setCalls(c.data.slice(0, 10));
      setSeries(ts.data);
    })();
  }, []);

  const max = Math.max(1, ...series.map((d) => d.calls));

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="chip">01 · {t("dashboard.title")}</div>
          <h1 className="mt-3 text-3xl lg:text-4xl font-extrabold text-white tracking-tight">{t("dashboard.title")}</h1>
          <p className="mt-1 text-slate-400 text-sm">{t("dashboard.sub")}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 anim-stagger">
        <StatCard icon={Phone} label={t("dashboard.total_calls")} value={stats?.total_calls ?? "—"} testid="stat-total" />
        <StatCard icon={Clock} label={t("dashboard.avg_duration")} value={stats?.avg_duration_sec ?? "—"} suffix="s" testid="stat-duration" />
        <StatCard icon={TrendingUp} label={t("dashboard.success_rate")} value={stats?.success_rate ?? "—"} suffix="%" testid="stat-success" />
        <StatCard icon={GitBranch} label={t("dashboard.escalation_rate")} value={stats?.escalation_rate ?? "—"} suffix="%" testid="stat-escalation" />
        <StatCard icon={Activity} label={t("dashboard.avg_confidence")} value={stats?.avg_confidence ?? "—"} testid="stat-confidence" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">{t("dashboard.timeseries")}</h3>
            <span className="text-xs font-mono text-slate-500">last 14 days</span>
          </div>
          <div className="mt-6 flex items-end gap-1.5 h-44">
            {series.length === 0 && <div className="text-slate-500 text-sm">—</div>}
            {series.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center justify-end gap-1 group">
                <div className="text-[10px] font-mono text-slate-500 opacity-0 group-hover:opacity-100 transition">{d.calls}</div>
                <div className="w-full rounded-t bg-gradient-to-t from-primary to-magenta" style={{ height: `${(d.calls / max) * 100}%`, minHeight: 4 }} />
                <div className="text-[10px] font-mono text-slate-500">{d.day.slice(5)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-bold text-white">{t("dashboard.recent_calls")}</h3>
          <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
            {calls.length === 0 && <div className="text-slate-500 text-sm">{t("dashboard.no_calls")}</div>}
            {calls.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 py-2 border-b border-white/5 last:border-0">
                <div className="min-w-0">
                  <div className="font-mono text-sm text-white truncate">{c.caller_number}</div>
                  <div className="text-xs text-slate-500 truncate">{c.transcript_summary}</div>
                </div>
                <StatusBadge status={c.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
